<template>
  <div class="member-layout" :class="{ 'is-desktop': isDesktop, 'is-mobile': !isDesktop, 'is-native': isMobile }">
    <!-- Desktop: Show sidebar -->
    <NavbarMember v-if="isDesktop && !hideNavbar" :forceDesktopAside="true" @open-qr="openQrModal" />
    
    <main class="member-main" :class="{ 'has-desktop-sidebar': isDesktop && !hideNavbar }">
      <OfflineBanner />
      <slot></slot>
    </main>
    
    <!-- Mobile: Show bottom navbar -->
    <NavbarMember v-if="!isDesktop && !hideNavbar" />
    
    <!-- QR Modal for Desktop -->
    <OQrCodeMemberModal
      v-if="isDesktop"
      :isVisible="qrCodeModal"
      :corporateLogo="corporateLogo"
      :memberId="memberId"
      :memberName="memberName"
      :corporateName="corporateName"
      :tierName="tierName"
      :tierIcon="tierIcon"
      :memberAvatar="memberAvatar"
      :memberVouchers="memberVouchers"
      :memberPoints="memberPoints"
      @close="qrCodeModal = false"
    />
  </div>
</template>

<script>
import NavbarMember from "./NavbarMember.vue";
import { OQrCodeMemberModal } from '@/components/OmnihControl';
import OfflineBanner from '@/components/OfflineBanner.vue';
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useTheme } from '@/composables/useTheme';
import { Capacitor } from '@capacitor/core';

export default {
  name: "MemberLayout",
  components: {
    NavbarMember,
    OQrCodeMemberModal,
    OfflineBanner,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const hideNavbar = computed(() => route.meta.hideNavbar || false);
    
    // QR Modal state
    const qrCodeModal = ref(false);
    const memberId = ref('');
    const memberName = ref('');
    const corporateName = ref('');
    const corporateLogo = ref('');
    const tierName = ref('');
    const tierIcon = ref('');
    const memberAvatar = ref('');
    
    const userStore = computed(() => store.state.Auth.currUser);
    
    const loadMemberProfile = (member) => {
      memberId.value = member?.member?.id || '';
      memberName.value = `${member?.title || member?.member?.title || ''} ${member?.first_name || member?.member?.first_name || ''} ${member?.last_name || member?.member?.last_name || ''}`.trim();
      corporateName.value = member?.member?.corporate_name || '';
      corporateLogo.value = member?.member?.corporate_logo || '';
      tierName.value = member?.member?.tier_name || '';
      tierIcon.value = member?.member?.tier?.icon?.image_url || store.state.ClientDashboard?.dashboard?.tier?.icon?.image_url || '';
      memberAvatar.value = member?.member?.member_avatar || '';
    };

    // Watch for changes in ClientDashboard store (Same fix as NavbarMember)
    watch(
      () => store.state.ClientDashboard?.dashboard,
      (newDashboard) => {
        if (newDashboard?.tier?.icon?.image_url) {
           tierIcon.value = newDashboard.tier.icon.image_url;
        }
      },
      { deep: true, immediate: true }
    );
    
    watch(
      () => route.fullPath,
      () => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          const memberMain = document.querySelector('.member-main');
          if (memberMain) {
            memberMain.scrollTop = 0;
          }
        }
      }
    );

    watch(
      userStore,
      (newVal) => {
        if (newVal?.member) {
          loadMemberProfile(newVal);
          const corporateId = newVal.member.corporate_id;
          if (corporateId) {
            // Apply dynamic theme
            const { applyTheme } = useTheme();
            applyTheme(corporateId);

            store.dispatch('FeatureFlag/fetchFeatureFlag', corporateId)
              .catch(error => {
                console.error('Error fetching feature flag in watcher:', error);
              });
          }
        }
      },
      { immediate: true, deep: true }
    );

    onMounted(async () => {
      // Ensure dashboard data (which contains corporateContactInfo) is loaded
      if (!store.state.ClientDashboard?.dashboard?.branch_images) {
        try {
          await store.dispatch('ClientDashboard/read');
        } catch (error) {
          console.error('Failed to fetch ClientDashboard data in MemberLayout:', error);
        }
      }
    });
    
    const openQrModal = async () => {
      // Ensure we have tier icon (Logic copied from NavbarMember.vue)
      
      // Try to get from store state first if not already set
      if (!tierIcon.value && store.state.ClientDashboard?.dashboard?.tier?.icon?.image_url) {
        tierIcon.value = store.state.ClientDashboard.dashboard.tier.icon.image_url;
      }

      if (!tierIcon.value) {
        try {
           const res = await store.dispatch('ClientDashboard/read');
           
           if (res?.data?.data?.tier?.icon?.image_url) {
             tierIcon.value = res.data.data.tier.icon.image_url;
           }
        } catch (e) {
           console.error('Failed to fetch dashboard for QR icon in MemberLayout', e);
        }
      }
      qrCodeModal.value = true;
    };
    
    // Computed untuk vouchers & points dari ClientDashboard store (ter-refresh setelah redeem/cancel)
    const memberVouchers = computed(() => {
      return store.state.ClientDashboard?.dashboard?.owned_vouchers ?? store.state.Auth.userInfo?.member?.filtered_vouchers?.length ?? 0;
    });
    
    const memberPoints = computed(() => {
      return store.state.ClientDashboard?.dashboard?.points ?? store.state.Auth.userInfo?.member?.points ?? 0;
    });
    
    // Check if running on mobile platform
    const isMobile = Capacitor.isNativePlatform();
    
    return {
      hideNavbar,
      isMobile,
      qrCodeModal,
      memberId,
      memberName,
      corporateName,
      corporateLogo,
      tierName,
      tierIcon,
      memberAvatar,
      memberVouchers,
      memberPoints,
      openQrModal,
    };
  },
  data() {
    return {
      isDesktop: window.innerWidth >= 992,
    };
  },
  mounted() {
    this.fetchTheme();
    this.fetchFeatureFlag();
    this.setupResizeListener();
  },
  beforeUnmount() {
    this.cleanupResizeListener();
  },
  methods: {
    fetchTheme() {
      const selectedBusinessProfile = JSON.parse(localStorage.getItem('selected_business_profile'));
      const corporateId = selectedBusinessProfile ? selectedBusinessProfile.corporate_id : null;
      
      if (corporateId) {
        const { applyTheme } = useTheme();
        applyTheme(corporateId);
      }
    },
    setupResizeListener() {
      this.handleResize = () => {
        this.isDesktop = window.innerWidth >= 992;
      };
      window.addEventListener('resize', this.handleResize);
    },
    cleanupResizeListener() {
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    },
    fetchFeatureFlag() {
      const corporateId = this.$store.state.Auth.currUser?.member?.corporate_id || 
                          this.$store.state.Auth.userInfo?.member?.corporate_id;
      
      if (corporateId) {
        this.$store.dispatch('FeatureFlag/fetchFeatureFlag', corporateId)
          .catch(error => {
            console.error('Error fetching feature flag:', error);
          });
      }
    },
  },
};
</script>

<style>
@import '../../assets/styles/member/index.css';
</style>

<style scoped>
.member-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.member-layout.is-desktop {
  flex-direction: row;
  align-items: stretch;
}

.member-main {
  flex: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.member-main.has-desktop-sidebar {
  margin-left: 0; /* Sidebar akan mengatur spacing sendiri */
}

/* Desktop layout dengan sidebar */
@media (min-width: 992px) {
  .member-layout.is-desktop {
    display: grid;
    grid-template-columns: auto 1fr;
    min-height: 100vh;
    overflow-x: visible;
  }
  
  .member-main.has-desktop-sidebar {
    background-color: #f5f6fa;
    overflow-y: auto;
  }
}

/* Mobile & Tablet layout dengan bottom navbar */
@media (max-width: 991px) {
  .member-layout.is-mobile {
    max-width: 100vw;
    overflow-x: hidden;
    padding-bottom: 96px; /* Space untuk bottom navbar */
  }
}
</style>
